import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../config/config';
import { useRefreshHeartbeatMutation } from '../graphql/heartbeat.graphql';
import { useGeolocationIntegration, useGeolocationStatus } from '../util/geolocation-context';

export enum SessionContextType {
    Gaming = 'gaming',
    Withdraw = 'withdraw',
    Deposit = 'deposit',
    Launch = 'launch',
}

export function useGeoLocationHeartbeat(type: SessionContextType = SessionContextType.Gaming) {
    const status = useGeolocationStatus();

    const integration = useGeolocationIntegration();
    const [_, execute] = useRefreshHeartbeatMutation();
    const auth = useAuth();
    const profileId = auth.user?.profile.user_profile_id as string;
    const lastHeartbeat = useRef(0);

    const sendHeartbeat = useCallback(
        (force = true) => {
            const expiry = integration?.getTokenExpiryTimestamp();

            if (!expiry || status.errors.length) {
                return;
            }

            if (!force && lastHeartbeat.current === expiry) {
                return;
            }

            execute({
                userProfileId: profileId,
                xTenantID: config.TENANT_ID,
                closeSessionParams: {
                    type,
                    errorCode: 'SESSION_CONTEXT_CLOSED_WAGERING_NOT_ALLOWED',
                    closeAtEpoch: integration?.getTokenExpiryTimestamp(),
                },
            });
            lastHeartbeat.current = expiry;
        },
        [execute, type, profileId, integration, status.errors.length],
    );

    useEffect(() => {
        sendHeartbeat(false);
    }, [status, sendHeartbeat]);

    return useMemo(
        () => ({
            sendHeartbeat,
        }),
        [sendHeartbeat],
    );
}
